import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CHUGUMI_QUESTIONS } from '../../data/chugumiQuestions';
import { useTranslation } from 'react-i18next';

const Question = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const isEnglish = (i18n.resolvedLanguage || i18n.language).startsWith('en');
    const currentQIdx = parseInt(id);

    // Initialize state from local storage or empty
    const [answers, setAnswers] = useState(() => {
        const saved = localStorage.getItem('chugumiTestAnswers');
        return saved ? JSON.parse(saved) : {};
    });

    const question = CHUGUMI_QUESTIONS.find(q => q.id === currentQIdx);

    useEffect(() => {
        localStorage.setItem('chugumiTestAnswers', JSON.stringify(answers));
    }, [answers]);

    // If invalid ID, redirect to intro
    if (!question) {
        return null;
    }

    const handleAnswer = (scoreValue) => {
        // Store the numeric score (1-5)
        const newAnswers = { ...answers, [currentQIdx]: scoreValue };
        setAnswers(newAnswers);

        if (currentQIdx < CHUGUMI_QUESTIONS.length) {
            navigate(`/chugumi-test/q/${currentQIdx + 1}`);
        } else {
            navigate('/chugumi-test/loading');
        }
    };

    const progress = (currentQIdx / CHUGUMI_QUESTIONS.length) * 100;
    const selectedAnswer = answers[currentQIdx];

    return (
        <div className="chugumi-container">
            <div className="question-card">
                <div className="progress-container">
                    <div className="progress-bar" style={{ width: `${progress}%` }}></div>
                    <span className="progress-text">{currentQIdx} / {CHUGUMI_QUESTIONS.length}</span>
                </div>

                <h2 className="question-text">{isEnglish ? question.question_en : question.question}</h2>

                <div className="scale-container">
                    {(isEnglish ? question.scale_en : question.scale).map((label, index) => {
                        const scoreValue = index + 1; // 1-5
                        const isSelected = selectedAnswer === scoreValue;

                        return (
                            <button
                                key={scoreValue}
                                className={`scale-btn ${isSelected ? 'selected' : ''}`}
                                onClick={() => handleAnswer(scoreValue)}
                            >
                                <div className="scale-circle">
                                    <span className="scale-number">{scoreValue}</span>
                                </div>
                                <span className="scale-label">{label}</span>
                            </button>
                        );
                    })}
                </div>

                {currentQIdx > 1 && (
                    <button className="back-btn" onClick={() => navigate(`/chugumi-test/q/${currentQIdx - 1}`)}>
                        {t('common.back', 'Back')}
                    </button>
                )}
            </div>

            <style jsx>{`
                .chugumi-container {
                    min-height: 100vh;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 1rem;
                    background: linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%);
                    color: white;
                }

                .question-card {
                    background: rgba(255, 255, 255, 0.05);
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 24px;
                    padding: 2rem 1.5rem;
                    width: 100%;
                    max-width: 600px;
                    min-height: 60vh;
                    display: flex;
                    flex-direction: column;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.3);
                }

                .progress-container {
                    margin-bottom: 3rem;
                    position: relative;
                }

                .progress-bar {
                    height: 6px;
                    background: linear-gradient(to right, #3b82f6, #8b5cf6);
                    border-radius: 10px;
                    transition: width 0.3s ease;
                }

                .progress-text {
                    position: absolute;
                    right: 0;
                    top: -25px;
                    font-size: 0.9rem;
                    color: #94a3b8;
                    font-weight: 500;
                }

                .question-text {
                    font-size: 1.4rem;
                    font-weight: 700;
                    line-height: 1.5;
                    margin-bottom: 2.5rem;
                    word-break: keep-all;
                    text-align: center;
                    flex: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .scale-container {
                    display: flex;
                    flex-direction: column;
                    gap: 0.75rem;
                    margin-bottom: 1rem;
                }

                .scale-btn {
                    width: 100%;
                    padding: 1rem 1.25rem;
                    background: rgba(255, 255, 255, 0.05);
                    border: 2px solid rgba(255, 255, 255, 0.1);
                    border-radius: 16px;
                    color: white;
                    font-size: 0.95rem;
                    cursor: pointer;
                    transition: all 0.2s;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                }

                .scale-btn:hover {
                    background: rgba(59, 130, 246, 0.15);
                    border-color: rgba(59, 130, 246, 0.4);
                    transform: translateX(4px);
                }

                .scale-btn.selected {
                    background: rgba(59, 130, 246, 0.25);
                    border-color: #3b82f6;
                    box-shadow: 0 0 20px rgba(59, 130, 246, 0.3);
                }

                .scale-circle {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 32px;
                    height: 32px;
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: 50%;
                    flex-shrink: 0;
                    transition: all 0.2s;
                }

                .scale-btn:hover .scale-circle,
                .scale-btn.selected .scale-circle {
                    background: rgba(59, 130, 246, 0.4);
                }

                .scale-number {
                    font-size: 0.9rem;
                    font-weight: 700;
                }

                .scale-label {
                    flex: 1;
                    text-align: left;
                    font-weight: 500;
                }

                .back-btn {
                    margin-top: 1.5rem;
                    background: transparent;
                    border: none;
                    color: #64748b;
                    cursor: pointer;
                    font-size: 0.9rem;
                    align-self: center;
                    padding: 0.5rem;
                }
                
                .back-btn:hover {
                    color: #94a3b8;
                    text-decoration: underline;
                }

                @media (max-width: 640px) {
                    .question-text {
                        font-size: 1.2rem;
                    }

                    .scale-btn {
                        padding: 0.85rem 1rem;
                        font-size: 0.85rem;
                    }

                    .scale-circle {
                        width: 28px;
                        height: 28px;
                    }

                    .scale-number {
                        font-size: 0.8rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default Question;
